import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, UserCheck, UserX } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input, Label, Select } from '../../components/ui/field';
import { ErrorState, LoadingState } from '../../components/ui/state';
import { api } from '../../shared/api';
import { copy, labelForStaffRole } from '../../shared/i18n';
import { useAuth } from '../auth/AuthProvider';

const fallbackRoles = ['Administrator', 'Receptionist', 'Staff'];

export function UserEditPage() {
  const { username = '' } = useParams();
  const decodedUsername = decodeURIComponent(username);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, refreshUser } = useAuth();
  const usersQuery = useQuery({ queryKey: ['staff-users'], queryFn: () => api.staffUsers.list({ limit: 100 }) });
  const rolesQuery = useQuery({ queryKey: ['staff-roles'], queryFn: api.staffRoles.list });
  const staffUser = useMemo(() => usersQuery.data?.items.find((item) => item.username === decodedUsername), [decodedUsername, usersQuery.data?.items]);
  const roles = rolesQuery.data?.items.length ? rolesQuery.data.items : fallbackRoles;
  const [role, setRole] = useState('Staff');
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  useEffect(() => {
    if (staffUser?.roles[0]) setRole(staffUser.roles[0]);
    if (staffUser) {
      const nameParts = (staffUser.name ?? '').trim().split(/\s+/);
      setGivenName(staffUser.givenName ?? nameParts[0] ?? '');
      setFamilyName(staffUser.familyName ?? nameParts.slice(1).join(' ') ?? '');
    }
  }, [staffUser]);

  const saveUser = useMutation({
    mutationFn: async () => {
      await api.staffUsers.updateName(decodedUsername, { givenName, familyName });
      await api.staffUsers.updateRole(decodedUsername, role);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['staff-users'] });
      if (user?.username === decodedUsername || user?.email === decodedUsername) await refreshUser();
      navigate('/admin/users');
    },
  });
  const setEnabled = useMutation({
    mutationFn: (enabled: boolean) => (enabled ? api.staffUsers.enable(decodedUsername) : api.staffUsers.disable(decodedUsername)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['staff-users'] });
      if (user?.username === decodedUsername || user?.email === decodedUsername) await refreshUser();
    },
  });

  if (usersQuery.isLoading || rolesQuery.isLoading) return <LoadingState label={copy.users.loading} />;
  if (usersQuery.isError) return <ErrorState message={usersQuery.error.message} />;
  if (rolesQuery.isError) return <ErrorState message={rolesQuery.error.message} />;
  if (!staffUser) return <ErrorState message="Usuario no encontrado." />;

  return (
    <div className="max-w-lg space-y-5">
      <div><h1 className="text-2xl font-semibold text-slate-950">Editar usuario</h1><p className="mt-1 text-sm text-slate-500">{staffUser.email ?? staffUser.username}</p></div>
      <Card><CardHeader><h2 className="text-sm font-semibold">Acceso</h2></CardHeader><CardContent className="grid gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-1.5"><Label htmlFor="givenName">Nombre</Label><Input id="givenName" value={givenName} onChange={(event) => setGivenName(event.target.value)} /></div>
          <div className="grid gap-1.5"><Label htmlFor="familyName">Apellido</Label><Input id="familyName" value={familyName} onChange={(event) => setFamilyName(event.target.value)} /></div>
        </div>
        <div className="grid gap-1.5"><Label htmlFor="role">{copy.common.role}</Label><Select id="role" value={role} onChange={(event) => setRole(event.target.value)}>{roles.map((item) => <option key={item} value={item}>{labelForStaffRole(item)}</option>)}</Select></div>
        {saveUser.isError ? <p className="text-sm text-rose-600">{saveUser.error.message}</p> : null}
        {setEnabled.isError ? <p className="text-sm text-rose-600">{setEnabled.error.message}</p> : null}
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={() => saveUser.mutate()} disabled={saveUser.isPending || !givenName || !familyName || !role}><Save className="h-4 w-4" aria-hidden="true" />Guardar cambios</Button>
          <Button type="button" variant="secondary" onClick={() => setEnabled.mutate(!staffUser.enabled)} disabled={setEnabled.isPending}>
            {staffUser.enabled ? <UserX className="h-4 w-4" aria-hidden="true" /> : <UserCheck className="h-4 w-4" aria-hidden="true" />}
            {staffUser.enabled ? copy.users.disable : copy.users.enable}
          </Button>
        </div>
      </CardContent></Card>
    </div>
  );
}
